import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  MapPin,
  Briefcase,
  Sparkles,
  Code2,
  Database,
  Globe,
  ChevronRight,
  Target,
  Lightbulb,
  Shield,
  Rocket,
  Heart,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import HeroImage from "../../assets/heroimage.jpg";

const skills = {
  Frontend: ["React", "Next.js", "Tailwind CSS", "Redux"],
  Backend: ["Laravel", "AdonisJS", "FastApi", "REST APIs"],
  Database: ["MySQL", "PostgreSQL"],
  Languages: ["PHP", "TypeScript", "Python", "Java", "JavaScript"],
  Tools: ["Git", "GitHub"],
};

const categoryIcons = {
  Frontend: <Code2 size={18} />,
  Backend: <Database size={18} />,
  Database: <Database size={18} />,
  Languages: <Globe size={18} />,
  Tools: <Sparkles size={18} />,
};

function useVisible(threshold = 0.15) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function SectionLabel({ text, icon: Icon }) {
  return (
    <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full mb-4 group hover:bg-blue-100 transition-all duration-300">
      {Icon && <Icon size={12} className="text-blue-600" />}
      <span className="w-1 h-1 rounded-full bg-blue-600"></span>
      <span className="text-blue-700 text-xs font-semibold tracking-wide">
        {text}
      </span>
    </div>
  );
}

function SkillTag({ skill, index }) {
  const delay = (index % 10) * 30;
  return (
    <span
      className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all duration-300 cursor-default"
      style={{
        transitionDelay: `${delay}ms`,
      }}
    >
      {skill}
    </span>
  );
}

export default function AboutSection() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [valuesRef, valuesVisible] = useVisible(0.1);
  const [focusRef, focusVisible] = useVisible(0.1);
  const [skillsRef, skillsVisible] = useVisible(0.1);
  const [goalRef, goalVisible] = useVisible(0.1);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const coreValues = [
    {
      icon: <Rocket size={20} />,
      title: "Mission-Driven Development",
      description:
        "I focus on building software that solves real-world problems and delivers practical value. I design and develop complete, production-ready applications, handling everything from the core logic to the user experience.",
    },
    {
      icon: <Shield size={20} />,
      title: "Clarity & Reliability",
      description:
        "My work is guided by a strong commitment to clarity, reliability, and impact. I believe technology should simplify complexity, not add to it, so I prioritize clean architecture, intuitive design, and efficient systems.",
    },
    {
      icon: <Lightbulb size={20} />,
      title: "Problem-Solving Mindset",
      description:
        "I approach every project by breaking down challenges, understanding real user needs, and turning ideas into practical, scalable solutions that people can actually use with ease.",
    },
    {
      icon: <Star size={20} />,
      title: "Continuous Improvement",
      description:
        "I value consistency and continuous improvement, always refining my skills and learning better ways to build. Writing maintainable code and creating structured systems are at the core of how I work.",
    },
    {
      icon: <Heart size={20} />,
      title: "Ownership & Accountability",
      description:
        "I believe in taking full responsibility for what I build and ensuring it meets a high standard of quality. I pay close attention to detail and take pride in delivering exceptional work.",
    },
    {
      icon: <Target size={20} />,
      title: "Empowering Solutions",
      description:
        "My mission is to create solutions that empower businesses and individuals to operate more efficiently and grow with confidence. I build tools that are dependable, impactful, and make a real difference.",
    },
  ];

  return (
    <main className="bg-white overflow-hidden">
      {/* ── HERO SECTION ── */}
      <div
        className="relative border-b border-gray-100"
        style={{
          backgroundImage: `url(${HeroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay so text stays readable */}
        <div className="absolute inset-0 bg-black/55" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-14">
          <div
            className={`text-center transition-all duration-1000 ease-out ${
              heroVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
              I'm a full-stack developer who builds products that{" "}
              <span className="text-blue-400">actually work</span>
            </h1>
            <div
              className={`flex flex-wrap items-center justify-center gap-3 transition-all duration-1000 ease-out delay-300 ${
                heroVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white border border-white/20 shadow-sm">
                <MapPin size={13} className="text-blue-400" />
                Nairobi, Kenya — available remotely
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white border border-white/20 shadow-sm">
                <Briefcase size={13} className="text-blue-400" />
                Open to freelance &amp; full-time roles
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT SECTIONS ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {/* ── CORE VALUES & APPROACH ── */}
        <div
          ref={valuesRef}
          className={`text-center  ${
            valuesVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          {/* <div className="flex justify-center">
            {/* <SectionLabel text="My Approach & Values" icon={Heart} />
          </div> */}{" "}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6 tracking-tight">
            How I build software that matters
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
            {coreValues.map((value, i) => (
              <div
                key={i}
                className={`group relative border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 cursor-pointer ${
                  valuesVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-xl transition-all duration-300 scale-x-0 group-hover:scale-x-100" />
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 group-hover:scale-110 transition-all text-blue-600">
                    {value.icon}
                  </div>
                  <h3 className="text-base font-bold text-black leading-tight">
                    {value.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── WHAT I FOCUS ON ── */}
        <div
          ref={focusRef}
          className={`text-center transition-all duration-1000 ease-out ${
            focusVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex justify-center">
            <SectionLabel text="What I focus on" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6 tracking-tight">
            End-to-end, front to back
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-left">
            {[
              {
                title: "Building complete products",
                desc: "I don't do just frontend or just backend. I take an idea from database design all the way to a working UI and I own every layer in between.",
              },
              {
                title: "Solving real problems",
                desc: "My projects are built around actual needs FreshDrop tackles e-commerce for the Kenyan market, Dakar360 gives growing businesses one place to manage everything.",
              },
              {
                title: "Clean, maintainable code",
                desc: "I care about how code is written, not just whether it works. Structure, readability, and consistency matter especially when working in a team.",
              },
              {
                title: "Collaboration",
                desc: "I'm actively building my skills in team workflows version control, clear communication, and writing code that other developers can understand and build on.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`group relative border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 cursor-pointer ${
                  focusVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-xl transition-all duration-300 scale-x-0 group-hover:scale-x-100" />
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-blue-50 group-hover:scale-110 transition-all">
                    <ChevronRight size={14} className="text-blue-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-black">
                    {item.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── SKILLS ── */}
        <div
          ref={skillsRef}
          className={`text-center transition-all duration-1000 ease-out ${
            skillsVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex justify-center">
            <SectionLabel text="Skills & Technologies" icon={Code2} />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6 tracking-tight">
            What I work with
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-left">
            {Object.entries(skills).map(([category, items], i) => (
              <div
                key={category}
                className={`group border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm hover:shadow-md transition-all duration-500 ${
                  skillsVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="text-blue-600">{categoryIcons[category]}</div>
                  <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase">
                    {category}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((skill, idx) => (
                    <SkillTag key={skill} skill={skill} index={idx} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── WHERE I'M HEADED ── */}
        <div
          ref={goalRef}
          className={`text-center transition-all duration-1000 ease-out ${
            goalVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <div className="relative group">
            <div className="absolute -inset-1 bg-blue-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition duration-1000"></div>
            <div className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md text-left">
              <div className="h-1 w-full bg-blue-600" />
              <div className="p-6 md:p-8 lg:p-10">
                <div className="flex justify-center">
                  <SectionLabel text="Where I'm headed" icon={Sparkles} />
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4 tracking-tight text-center">
                  The goal —{" "}
                  <span className="text-blue-600">
                    senior full-stack developer
                  </span>
                </h2>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mb-6 text-center">
                  I want to grow into a senior developer who builds products
                  that matter especially in the African tech space. I'm not
                  chasing a title. I want the depth, the judgment, and the track
                  record that comes from consistently shipping real, working
                  software. If you're building something meaningful and need a
                  developer who cares about the work, let's talk.
                </p>
                <div className="flex justify-center">
                  <Link
                    to="/contact"
                    className="group relative inline-flex items-center gap-2 bg-black text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-blue-600 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <span className="relative z-10">Get in touch</span>
                    <ArrowRight
                      size={14}
                      className="relative z-10 group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
